import { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Clock, ChefHat, Leaf, Dog, Printer, FileDown } from 'lucide-react';
import { Seo, SITE_URL } from '../components/Seo';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { Disclaimer } from '../components/ArticleParts';
import { Markdown } from '../components/Markdown';
import { recipes as staticRecipes } from '../content/recipes';
import NotFound from './NotFound';

const API = process.env.REACT_APP_BACKEND_URL;

function absUrl(url) {
  if (!url) return '';
  return url.startsWith('/api/') ? `${API}${url}` : url;
}

function normalize(r) {
  const img = r.hauptbild
    ? { src: absUrl(r.hauptbild.url), alt: r.hauptbild.alt || '', caption: r.hauptbild.caption || '' }
    : r.image
      ? { src: r.image.src, srcSet: r.image.srcSet, alt: r.image.alt, width: r.image.width, height: r.image.height }
      : null;
  return {
    slug: r.slug,
    title: r.title || r.name,
    kurzbeschreibung: r.kurzbeschreibung,
    einleitung: r.einleitung,
    image: img,
    weitereBilder: (r.weitere_bilder || []).map((b) => ({ src: absUrl(b.url), alt: b.alt || '', caption: b.caption || '' })),
    kategorie: r.kategorie,
    glutenfrei: r.glutenfrei,
    schwierigkeit: r.schwierigkeit,
    zubereitungszeit: r.zubereitungszeit,
    gesamtzeit: r.gesamtzeit,
    portionen: r.portionen,
    hundegroesse: r.hundegroesse || r.groessenEmpfehlung,
    hundegewicht: r.hundegewicht,
    zutaten: r.zutaten || [],
    zubereitung: r.zubereitung || [],
    hinweise: r.hinweise || [],
    tipps: r.tipps || '',
    allergene: r.allergene || [],
    aufbewahrung: r.aufbewahrung,
    autor: r.autor,
    veroeffentlicht: (r.veroeffentlicht_am || r.veroeffentlicht || '').slice(0, 10),
    geaendert: (r.updated_at || r.geaendert || '').slice(0, 10),
    status: r.status || 'published',
    seo: r.seo || {},
  };
}

export default function RezeptDetail() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const isPreview = params.get('vorschau') === 'admin';
  const [recipe, setRecipe] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let active = true;
    setRecipe(null);
    setNotFound(false);
    fetch(`${API}/api/recipes/${slug}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data) => { if (active) setRecipe(normalize(data)); })
      .catch(async () => {
        if (isPreview) {
          try {
            const res = await fetch(`${API}/api/admin/recipes/by-slug/${slug}`, { credentials: 'same-origin' });
            if (res.ok) {
              const data = await res.json();
              if (active) setRecipe(normalize(data));
              return;
            }
          } catch { /* fall through */ }
        }
        const fallback = staticRecipes.find((r) => r.slug === slug);
        if (fallback) {
          if (active) setRecipe(normalize(fallback));
        } else if (active) setNotFound(true);
      });
    return () => { active = false; };
  }, [slug, isPreview]);

  if (notFound) return <NotFound />;
  if (!recipe) {
    return (
      <div className="container-site py-20" role="status">
        <p className="text-brand-brown-soft">Rezept wird geladen …</p>
      </div>
    );
  }

  const seoTitle = recipe.seo.seo_title || `${recipe.title} | Rezept | Felix Genuss`;
  const seoDesc = recipe.seo.meta_description || recipe.kurzbeschreibung;

  const jsonLd = recipe.status === 'published' ? {
    '@context': 'https://schema.org',
    '@type': 'Recipe',
    name: recipe.title,
    description: recipe.kurzbeschreibung,
    ...(recipe.image ? { image: [`${SITE_URL}${recipe.image.src.startsWith('http') ? new URL(recipe.image.src).pathname : recipe.image.src}`] } : {}),
    author: { '@type': 'Organization', name: recipe.autor || 'Felix Genuss Redaktion', url: SITE_URL },
    ...(recipe.veroeffentlicht ? { datePublished: recipe.veroeffentlicht } : {}),
    ...(recipe.geaendert ? { dateModified: recipe.geaendert } : {}),
    ...(recipe.zubereitungszeit ? { prepTime: 'PT10M' } : {}),
    ...(recipe.gesamtzeit ? { totalTime: 'PT15M' } : {}),
    ...(recipe.portionen ? { recipeYield: recipe.portionen } : {}),
    recipeCategory: recipe.kategorie,
    keywords: ['Hunderezept', recipe.glutenfrei ? 'glutenfrei' : null, 'kleine Hunde'].filter(Boolean).join(', '),
    ...(recipe.glutenfrei ? { suitableForDiet: 'https://schema.org/GlutenFreeDiet' } : {}),
    recipeIngredient: recipe.zutaten.map((z) => `${z.menge} ${z.zutat}`),
    recipeInstructions: recipe.zubereitung.map((step, i) => ({ '@type': 'HowToStep', position: i + 1, text: step })),
  } : null;

  return (
    <>
      <Seo
        title={seoTitle}
        description={seoDesc}
        path={`/rezepte/${recipe.slug}`}
        image={recipe.seo.og_image || (recipe.image && recipe.image.src.startsWith('/assets') ? recipe.image.src : undefined)}
        jsonLd={jsonLd}
      />
      <div className="no-print">
        <Breadcrumbs items={[{ label: 'Startseite', href: '/' }, { label: 'Rezepte', href: '/rezepte' }, { label: recipe.title }]} />
      </div>

      {isPreview && recipe.status !== 'published' && (
        <p role="status" className="container-site mt-4 rounded-sm border border-brand-gold bg-brand-beige px-4 py-3 text-sm font-semibold text-brand-brown" data-testid="preview-banner">
          Admin-Vorschau: Dieses Rezept ist noch nicht veröffentlicht und für Besucher nicht sichtbar.
        </p>
      )}

      <div className="print-only container-site" aria-hidden="true">
        <img src="/assets/brand/logo-256.webp" alt="" width="64" height="64" />
        <p>Felix Genuss – Sternenküche im Napf</p>
      </div>

      <article className="container-site mt-10" data-testid="recipe-detail">
        <div className="grid gap-10 lg:grid-cols-2 lg:gap-16">
          <div>
            <p className="eyebrow">Rezept · {recipe.kategorie}</p>
            <h1 className="mt-3 font-serif text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl">{recipe.title}</h1>
            <div className="mt-5 text-brand-brown/85">
              <Markdown>{recipe.einleitung}</Markdown>
            </div>
            <dl className="mt-8 grid grid-cols-2 gap-4 border-y border-brand-line py-6 sm:grid-cols-4">
              <div>
                <dt className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-brand-brown-soft"><Clock className="h-4 w-4 text-brand-olive" aria-hidden="true" /> Gesamtzeit</dt>
                <dd className="mt-1 font-serif text-lg font-bold">{recipe.gesamtzeit || '–'}</dd>
              </div>
              <div>
                <dt className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-brand-brown-soft"><ChefHat className="h-4 w-4 text-brand-olive" aria-hidden="true" /> Aufwand</dt>
                <dd className="mt-1 font-serif text-lg font-bold">{recipe.schwierigkeit}</dd>
              </div>
              <div>
                <dt className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-brand-brown-soft"><Leaf className="h-4 w-4 text-brand-olive" aria-hidden="true" /> Eigenschaft</dt>
                <dd className="mt-1 font-serif text-lg font-bold">{recipe.glutenfrei ? 'Glutenfrei' : 'Mit Gluten'}</dd>
              </div>
              <div>
                <dt className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-brand-brown-soft"><Dog className="h-4 w-4 text-brand-olive" aria-hidden="true" /> Geeignet für</dt>
                <dd className="mt-1 font-serif text-lg font-bold">{[recipe.hundegroesse, recipe.hundegewicht].filter(Boolean).join(', ') || 'Alle Hunde'}</dd>
              </div>
            </dl>
            <p className="mt-4 text-sm text-brand-brown-soft">
              {recipe.portionen && <>Ergibt: {recipe.portionen} · </>}
              Von <span className="font-semibold">{recipe.autor}</span>
              {recipe.veroeffentlicht && <> · Veröffentlicht am <time dateTime={recipe.veroeffentlicht}>{new Date(recipe.veroeffentlicht).toLocaleDateString('de-CH', { day: 'numeric', month: 'long', year: 'numeric' })}</time></>}
            </p>
            <div className="no-print mt-6 flex flex-wrap gap-3">
              <button type="button" onClick={() => window.print()} className="btn-secondary" data-testid="recipe-print-button">
                <Printer className="h-4 w-4" aria-hidden="true" /> Rezept drucken
              </button>
              <a href={`${API}/api/recipes/${recipe.slug}/pdf`} className="btn-primary" data-testid="recipe-pdf-button">
                <FileDown className="h-4 w-4" aria-hidden="true" /> Rezept als PDF
              </a>
            </div>
          </div>
          {recipe.image && (
            <figure>
              <img
                src={recipe.image.src}
                srcSet={recipe.image.srcSet}
                sizes="(min-width: 1024px) 50vw, 100vw"
                width={recipe.image.width}
                height={recipe.image.height}
                alt={recipe.image.alt}
                fetchPriority="high"
                className="h-auto w-full border border-brand-gold/30"
                data-testid="recipe-image"
              />
              {(recipe.image.caption || recipe.status === 'published') && (
                <figcaption className="mt-3 text-sm text-brand-brown-soft">{recipe.image.caption || 'Felix-approved: von Felix höchstpersönlich probiert.'}</figcaption>
              )}
            </figure>
          )}
        </div>

        <div className="mt-16 grid gap-12 lg:grid-cols-[1fr_1.4fr] lg:gap-16">
          <section aria-labelledby="zutaten-heading">
            <h2 id="zutaten-heading" className="font-serif text-2xl font-bold sm:text-3xl">Zutaten</h2>
            <ul className="mt-6 divide-y divide-brand-line border-y border-brand-line">
              {recipe.zutaten.map((z, i) => (
                <li key={`${z.zutat}-${i}`} className="flex items-baseline gap-4 py-3">
                  <span className="w-20 shrink-0 font-serif text-lg font-bold text-brand-olive">{z.menge}</span>
                  <span className="text-brand-brown/90">{z.zutat}</span>
                </li>
              ))}
            </ul>
            {recipe.allergene.length > 0 && (
              <p className="mt-4 text-sm text-brand-brown-soft">
                Mögliche Allergene: {recipe.allergene.join(', ')}. Bitte bekannte Unverträglichkeiten deines Hundes berücksichtigen.
              </p>
            )}
          </section>

          <section aria-labelledby="zubereitung-heading">
            <h2 id="zubereitung-heading" className="font-serif text-2xl font-bold sm:text-3xl">Zubereitung</h2>
            <ol className="mt-6 space-y-6">
              {recipe.zubereitung.map((step, i) => (
                <li key={i} className="flex gap-5">
                  <span aria-hidden="true" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-brand-gold font-serif text-lg font-bold text-brand-gold-text">
                    {i + 1}
                  </span>
                  <p className="pt-1.5 leading-relaxed text-brand-brown/90">{step}</p>
                </li>
              ))}
            </ol>
          </section>
        </div>

        <section className="mt-16 grid gap-6 md:grid-cols-2" aria-labelledby="hinweise-heading">
          {recipe.hinweise.length > 0 && (
            <div className="border border-brand-line bg-brand-beige p-7">
              <h2 id="hinweise-heading" className="font-serif text-2xl font-bold">Felix' Hinweise</h2>
              <ul className="mt-4 list-disc space-y-2 pl-5 text-sm leading-relaxed text-brand-brown/90 marker:text-brand-gold">
                {recipe.hinweise.map((h, i) => (
                  <li key={i}>{h}</li>
                ))}
              </ul>
            </div>
          )}
          <div className="border border-brand-line p-7">
            {recipe.aufbewahrung && (
              <>
                <h2 className="font-serif text-2xl font-bold">Aufbewahrung</h2>
                <p className="mt-4 text-sm leading-relaxed text-brand-brown/90">{recipe.aufbewahrung}</p>
              </>
            )}
            <p className="mt-6 text-sm leading-relaxed text-brand-brown/90">
              Mehr Hintergrund dazu,{' '}
              <Link to="/wissen/lebensmittel" className="link-inline">welche Lebensmittel sich für Hunde eignen</Link>, findest du in unserem Wissensbereich.
            </p>
          </div>
        </section>

        {recipe.tipps && (
          <section className="mt-10" aria-labelledby="tipps-heading">
            <h2 id="tipps-heading" className="font-serif text-2xl font-bold sm:text-3xl">Zusätzliche Tipps</h2>
            <Markdown>{recipe.tipps}</Markdown>
          </section>
        )}

        {recipe.weitereBilder.length > 0 && (
          <section className="mt-10" aria-labelledby="bilder-heading">
            <h2 id="bilder-heading" className="font-serif text-2xl font-bold sm:text-3xl">Impressionen</h2>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {recipe.weitereBilder.map((b, i) => (
                <figure key={i}>
                  <img src={b.src} alt={b.alt} loading="lazy" className="h-auto w-full border border-brand-gold/30" />
                  {b.caption && <figcaption className="mt-2 text-sm text-brand-brown-soft">{b.caption}</figcaption>}
                </figure>
              ))}
            </div>
          </section>
        )}

        <Disclaimer />
        <p className="print-only mt-8 text-sm" aria-hidden="true">
          Quelle: felix-genuss.ch/rezepte/{recipe.slug} · © Felix Genuss
        </p>
      </article>
    </>
  );
}
